/**
 * Notification Service
 * Handles notification parsing and business logic
 * Follows Single Responsibility Principle
 */

export interface ParsedNotification {
  raw: any;
  id: number;
  action: number;
  status: number;
  actionData: string;
  targetData: string;
  paramId: string;
  paramValue: string;
  time: string;
}

export interface NotificationMessage {
  message: string;
  status: 'pending' | 'accepted' | 'declined';
}

/**
 * Notification Parser
 * Extracts notification parsing logic from components
 */
export class NotificationParser {
  /**
   * Parse notification data from API response
   */
  static parseNotification(item: any): ParsedNotification {
    const [actionData, targetData] = (item?.data || "").split(">>");
    const [paramId, paramValue] = (item?.param || "").split(">>");

    return {
      raw: item,
      id: Number(item?.id),
      action: Number(item?.action),
      status: Number(item?.status),
      actionData: actionData || "",
      targetData: targetData || "",
      paramId: paramId || "",
      paramValue: paramValue || "",
      time: "Just now",
    };
  }

  /**
   * Get human-readable message from notification
   */
  static getMessage(notification: ParsedNotification): string {
    const { action, status, actionData, targetData } = notification;

    if (status === 0) {
      switch (action) {
        case 3: return `Swimmer ${actionData} wants to connect with you`;
        case 1: return `Swimmer ${actionData} wants to be your friend`;
        case 2: return `Swimmer ${actionData} wants to join the team ${targetData}`;
        case 4: return `Coach ${actionData} wants you to join the team ${targetData}`;
        case 5: return `Coach ${actionData} wants to connect with you`;
        default: return "New notification";
      }
    }

    if (status === 1) {
      switch (action) {
        case 3: return `You connected with coach ${actionData}`;
        case 1: return `You are now friend with swimmer ${actionData}`;
        case 2: return `You joined the team ${actionData}`;
        case 4: return `Swimmer ${actionData} was added to the team ${targetData}`;
        case 5: return `You connected with swimmer ${actionData}`;
        default: return "Notification accepted";
      }
    }

    if (status === 2) {
      switch (action) {
        case 3: return `You were declined to connect with coach ${actionData}`;
        case 1: return `You were declined to be friend with swimmer ${actionData}`;
        case 2: return `You were declined to join the team ${actionData}`;
        case 4: return `Swimmer ${actionData} declined invitation to team ${targetData}`;
        case 5: return `Swimmer ${actionData} declined connecting with you`;
        default: return "Notification declined";
      }
    }

    return "";
  }

  /**
   * Get notification status label
   */
  static getStatusLabel(notification: ParsedNotification): NotificationMessage['status'] {
    if (notification.status === 0) return 'pending';
    if (notification.status === 1) return 'accepted';
    return 'declined';
  }
}

